import type { DocKey } from './keys';
import { isPlainObject, sameJson } from './storage';

/**
 * The account's five documents, and changes to them as patches.
 *
 * A document is stored whole (`PUT /me/documents/:key`, with the version it
 * was based on), but a change is kept as a patch: these entries set, those
 * removed. On a 409 the patch is applied again on top of the server's newer
 * copy, so a change made in another tab or on another device survives beside
 * this one, and an entry this device deleted stays deleted.
 *
 *   personalEvents   an array of drafts, patched by `id`
 *   attendance, travelLegs, eventEdits   objects, patched by key
 *   home             one value, patched whole (entry `HOME_ENTRY`)
 */

export type PatchOp = { set: unknown } | { del: true };
export type DocPatch = Record<string, PatchOp>;

export const HOME_ENTRY = 'home';

export const EMPTY_BODY: Record<DocKey, unknown> = {
  personalEvents: [],
  attendance: {},
  travelLegs: {},
  home: null,
  eventEdits: {},
};

/** A body of the right type, whatever the server or device held. */
export function bodyOr(key: DocKey, body: unknown): unknown {
  if (key === 'home') return body ?? null;
  if (key === 'personalEvents') return Array.isArray(body) ? body : [];
  return isPlainObject(body) ? body : {};
}

const idOf = (item: unknown): string | null =>
  isPlainObject(item) && typeof item.id === 'string' ? item.id : null;

/** What changed between two copies of a document. */
export function docPatch(key: DocKey, prev: unknown, next: unknown): DocPatch {
  const patch: DocPatch = {};

  if (key === 'home') {
    if (!sameJson(prev ?? null, next ?? null)) patch[HOME_ENTRY] = { set: next ?? null };
    return patch;
  }

  if (key === 'personalEvents') {
    const before = new Map<string, unknown>();
    for (const item of bodyOr(key, prev) as unknown[]) {
      const id = idOf(item);
      if (id !== null && !before.has(id)) before.set(id, item);
    }
    const seen = new Set<string>();
    for (const item of bodyOr(key, next) as unknown[]) {
      const id = idOf(item);
      if (id === null || seen.has(id)) continue;
      seen.add(id);
      if (!before.has(id) || !sameJson(before.get(id), item)) patch[id] = { set: item };
    }
    for (const id of before.keys()) if (!seen.has(id)) patch[id] = { del: true };
    return patch;
  }

  const before = bodyOr(key, prev) as Record<string, unknown>;
  const after = bodyOr(key, next) as Record<string, unknown>;
  for (const [k, v] of Object.entries(after)) {
    if (!(k in before) || !sameJson(before[k], v)) patch[k] = { set: v };
  }
  for (const k of Object.keys(before)) if (!(k in after)) patch[k] = { del: true };
  return patch;
}

export const isEmptyPatch = (patch: DocPatch): boolean => Object.keys(patch).length === 0;

/** A newer patch over an older one: per entry, the later op wins. */
export const mergePatch = (older: DocPatch, newer: DocPatch): DocPatch => ({ ...older, ...newer });

/** A document with a patch applied. Never mutates `body`. */
export function applyDocPatch(key: DocKey, body: unknown, patch: DocPatch): unknown {
  if (key === 'home') {
    const op = patch[HOME_ENTRY];
    if (!op) return bodyOr(key, body);
    return 'del' in op ? null : (op.set ?? null);
  }

  if (key === 'personalEvents') {
    const items = bodyOr(key, body) as unknown[];
    const placed = new Set<string>();
    const out: unknown[] = [];
    for (const item of items) {
      const id = idOf(item);
      const op = id === null ? undefined : patch[id];
      if (!op) {
        out.push(item);
      } else if ('set' in op && !placed.has(id as string)) {
        // An edit keeps its place in the list.
        out.push(op.set);
        placed.add(id as string);
      }
    }
    for (const [id, op] of Object.entries(patch)) {
      if ('set' in op && !placed.has(id)) out.push(op.set);
    }
    return out;
  }

  const out = { ...(bodyOr(key, body) as Record<string, unknown>) };
  for (const [k, op] of Object.entries(patch)) {
    if ('del' in op) delete out[k];
    else out[k] = op.set;
  }
  return out;
}
